import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import {
  FaBookOpen,
  FaCheckCircle,
  FaChevronDown,
  FaCalendar,
  FaClipboardList,
  FaInfoCircle
} from 'react-icons/fa';
import { FiExternalLink } from 'react-icons/fi';
import RichTextRenderer from '../common/RichTextRenderer';
import { getCourseContent, isContentCompleted } from '../../utils/progressUtils';

/**
 * CourseDetailModal Component
 * Displays detailed information about a course, event, or information item
 * Shows course content with accordion, lessons, missions, and event details
 *
 * @param {Object} props
 * @param {Object} props.selectedCourse - The selected course/event/information to display
 * @param {Function} props.onClose - Handler to close the modal
 * @param {Object} props.userProgress - User progress data
 * @param {string} props.userCurrentContent - Current content ID
 * @param {Object} props.userCurrentCourse - Current course object
 * @param {number} props.courseProgress - Current course progress percentage
 * @param {Function} props.isCourseCompletedLocal - Function to check if course is completed
 * @param {Set} props.expandedLessons - Set of expanded lesson IDs
 * @param {Function} props.setExpandedLessons - Update expanded lessons
 * @param {Function} props.toggleLessonAccordion - Toggle lesson accordion handler
 */
const CourseDetailModal = ({
  selectedCourse,
  onClose,
  userProgress,
  userCurrentContent,
  userCurrentCourse,
  courseProgress,
  isCourseCompletedLocal,
  expandedLessons,
  setExpandedLessons,
  toggleLessonAccordion,
  onLessonClick
}) => {
  const [descExpanded, setDescExpanded] = useState(false);

  React.useEffect(() => {
    setDescExpanded(false);
  }, [selectedCourse?._id]);

  if (!selectedCourse) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-2 sm:p-4"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={(e) => {
        // Close modal when clicking backdrop
        if (e.target === e.currentTarget) {
          setExpandedLessons(new Set()); // Reset expanded lessons
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden course-event-modal">
        {/* Fixed Header with Thumbnail */}
        <div className="flex-shrink-0">
          <div className="relative">
            <div className="w-full bg-gray-100 aspect-[16/9] flex items-center justify-center">
              {selectedCourse.thumbnail && (selectedCourse.thumbnail.url || selectedCourse.thumbnail.data) ? (
                <img
                  src={selectedCourse.thumbnail.url || `data:${selectedCourse.thumbnail.contentType};base64,${selectedCourse.thumbnail.data}`}
                  alt={selectedCourse.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 bg-[#F8F2FF] flex items-center justify-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#1086a4] bg-opacity-10 rounded-full flex items-center justify-center">
                    <FaBookOpen className="w-8 h-8 sm:w-10 sm:h-10 text-[#1086a4]" />
                  </div>
                </div>
              )}

              {/* Status Overlay */}
              <div className="absolute top-3 right-3 bg-black bg-opacity-50 backdrop-blur-sm text-white px-3 py-1 rounded-lg text-sm">
                {(() => {
                  // Check if this is an event/information or course
                  if (selectedCourse.type === 'event' || selectedCourse.type === 'information' || selectedCourse.date) {
                    // This is an event or information
                    if (selectedCourse.type === 'information') {
                      return (
                        <div className="flex items-center gap-1">
                          <FaInfoCircle className="w-3 h-3" />
                          <span>Information</span>
                        </div>
                      );
                    } else {
                      return "Event";
                    }
                  } else {
                    // This is a course
                    const isCompleted = isCourseCompletedLocal(selectedCourse._id);
                    const isCurrent = userCurrentCourse?._id === selectedCourse._id;

                    if (isCompleted) {
                      return "Completed";
                    } else if (isCurrent) {
                      return `In Progress - ${courseProgress}%`;
                    } else {
                      return "Upcoming";
                    }
                  }
                })()}
              </div>

              {/* Close Button - Always accessible */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedLessons(new Set()); // Reset expanded lessons
                  onClose();
                }}
                className="absolute top-3 left-3 bg-black bg-opacity-50 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-opacity-70 transition-all z-10 close-button"
              >
                ×
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 custom-scrollbar">
          {/* Title */}
          <h3 className="text-xl sm:text-2xl font-bold text-[#054581] mb-3 sm:mb-4 modal-title">
            {selectedCourse.title}
          </h3>

          {/* Description */}
          <div className="mb-4 sm:mb-6">
            <div className={!descExpanded ? 'line-clamp-3 overflow-hidden' : ''}>
              <RichTextRenderer
                content={selectedCourse.description}
                className="text-[#1e2d40] modal-description"
                fontSize="14px"
                style={{ fontSize: 'clamp(14px, 2.5vw, 16px)' }}
                allowInlineStyles={true}
              />
            </div>
            {selectedCourse.description && selectedCourse.description.length > 150 && (
              <button
                type="button"
                onClick={() => setDescExpanded(prev => !prev)}
                className="text-sm text-[#1086a4] font-medium mt-1 hover:underline focus:outline-none"
              >
                {descExpanded ? 'See less' : 'See more'}
              </button>
            )}
          </div>

          {/* Event/Information-specific content */}
          {(selectedCourse.type === 'event' || selectedCourse.type === 'information' || selectedCourse.date) && (
            <div className="mb-4 sm:mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <FaCalendar className="text-[#1086a4] w-4 h-4" />
                <span className="font-semibold text-[#054581]">
                  {selectedCourse.type === 'information' ? 'Information Details' : 'Event Details'}
                </span>
              </div>
              <div className="text-sm text-[#1e2d40]">
                {selectedCourse.type === 'event' && selectedCourse.date && (
                  <div className="space-y-1">
                    <p><strong>Date & Time:</strong> {new Date(selectedCourse.date).toLocaleString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                      timeZone: selectedCourse.timezone || 'UTC'
                    })}</p>
                    {selectedCourse.timezone && (
                      <p><strong>Timezone:</strong> {selectedCourse.timezone.replace('_', ' ')}</p>
                    )}
                  </div>
                )}
                {selectedCourse.program && (
                  <p><strong>Program:</strong> {selectedCourse.program.title}</p>
                )}
              </div>
            </div>
          )}

          {/* Course-specific content */}
          {!selectedCourse.type && !selectedCourse.date && (
            <div className="space-y-4">
              <h4 className="font-semibold text-lg text-[#054581]">Course Content:</h4>

              {/* Get mixed content for the course */}
              {(() => {
                const courseContent = getCourseContent(selectedCourse);
                // Exclude reinforcements from navigable content count
                const navigableContent = courseContent.filter(item => item.type !== 'reinforcement');
                const totalContent = navigableContent.length;
                const lessonsCount = courseContent.filter(item => item.type === 'lesson').length;
                const submissionFormsCount = courseContent.filter(item => item.type === 'submissionForm').length;

                return (
                  <>
                    <div className="space-y-1 mb-4">
                      {navigableContent.map((content, index) => {
                        const isCompleted = isContentCompleted(content, userProgress);
                        const isCurrent = userCurrentContent === content._id;
                        const canClick = isCompleted && onLessonClick;

                        return (
                          <div key={content._id} className="border border-gray-200 rounded-lg overflow-hidden">
                            {/* Content Header */}
                            <div
                              className={`flex items-center space-x-3 p-3 transition-colors ${
                                canClick
                                  ? 'cursor-pointer hover:bg-gray-50'
                                  : ''
                              } ${
                                isCompleted
                                  ? 'bg-green-50 border-green-200'
                                  : isCurrent
                                    ? 'bg-blue-50 border-blue-200'
                                    : 'bg-gray-50'
                              }`}
                              onClick={() => canClick && onLessonClick(content)}
                            >
                              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                content.type === 'lesson'
                                  ? 'bg-[#1086a4] bg-opacity-10 text-[#1086a4]'
                                  : 'bg-purple-500 bg-opacity-10 text-purple-600'
                              }`}>
                                {index + 1}
                              </span>

                              <div className="flex items-center space-x-2 flex-1">
                                <span className="text-[#1e2d40] text-sm sm:text-base">{content.title}</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  content.type === 'lesson'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-purple-100 text-purple-800'
                                }`}>
                                  {content.type === 'lesson' ? 'Lesson' : 'Mission'}
                                </span>
                              </div>

                              <div className="flex items-center space-x-2">
                                {content.type === 'lesson' && content.slug && (
                                  <a
                                    href={`/lesson/${content.slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-[#1086a4] hover:bg-[#1086a4]/10 transition-colors"
                                    title="Open lesson in new tab"
                                  >
                                    <FiExternalLink className="w-3.5 h-3.5" />
                                  </a>
                                )}

                                {isCompleted && (
                                  <FaCheckCircle className="w-5 h-5 text-green-500" />
                                )}

                                {isCurrent && !isCompleted && (
                                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                                )}

                                {canClick && (
                                  <FaChevronDown className="w-4 h-4 text-gray-400" />
                                )}
                              </div>
                            </div>

                          </div>
                        );
                      })}
                    </div>

                    {/* Content Summary */}
                    <div className="flex items-center justify-between text-sm text-gray-500 bg-gray-50 px-4 py-3 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <FaBookOpen className="w-4 h-4 mr-2" />
                          <span>{lessonsCount} Lessons</span>
                        </div>
                        {submissionFormsCount > 0 && (
                          <div className="flex items-center">
                            <FaClipboardList className="w-4 h-4 mr-2" />
                            <span>{submissionFormsCount} Missions</span>
                          </div>
                        )}
                      </div>
                      <span>Total: {totalContent} Items</span>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CourseDetailModal;
